import berlinBg from '../assets/berlin-bg.jpg';
import aboutHeroBg from '../assets/checkpoint-charlie-bg.jpg';
import { useTranslations } from '../i18n';

type AboutImageCopy = {
  heading: string;
  body: string;
  alt?: string;
  src?: string;
  placeholder?: string;
};

const FALLBACK_PARAGRAPHS = [
  'Look closely at the vintage newspapers and photographs and you enter another era. The name takes us back to Checkpoint Charlie, the border crossing that separated East and West Berlin and changed the lives of everyone who made it to the other side.',
  'For two worlds divided by a single wall, crossing it was a dream. While the press in the “paradise” insisted the West was misery, reality was different — many lost their lives to border guards simply for longing for freedom. They risked everything to take those few steps toward a new life.',
  'To younger guests this may sound unimaginable. That is why the restaurant carries this name: not to dwell on sorrow, but to remember something precious — the right to choose where to live, to travel, and to gather without permission.',
  'Our name is a tribute to human courage. Two photographs in the dining room keep that memory alive better than any story. One shows a young East German soldier abandoning his post with a single leap that remade his future. The other, above the exit, captures an older man emerging from a tunnel dug by students. The moment he reaches daylight is the very essence of the unconquered human spirit.',
  'These images stand as a quiet reminder to value the freedom we enjoy today. They remind us to cherish every occasion to meet, laugh, and choose what to share at the table. When we treat those moments as a gift, they become true happiness. Welcome.',
];

const FALLBACK_IMAGES: Record<'soldier' | 'tunnel', AboutImageCopy> = {
  soldier: {
    heading: 'Leap to freedom',
    body: 'Conrad Schumann, the East German guard captured mid-jump on August 15, 1961, moments before landing in West Berlin.',
    placeholder: 'Historic photograph coming soon',
  },
  tunnel: {
    heading: 'Arrival from the tunnel',
    body: 'A father emerging into the West after crawling through a student-dug tunnel — an enduring portrait of resilience.',
    placeholder: 'Historic photograph coming soon',
  },
};

export function AboutPage() {
  const { t } = useTranslations();
  const heading = t('about.heading', 'The spirit behind CheckPoint Charly') as string;
  const intro = t(
    'about.intro',
    'Welcome to CheckPoint Charly — a place created for meaningful conversations, slow evenings, and the simple joys of being together. It is where friends linger over a meal, jazz floats through the room, and every visit feels personal.'
  ) as string;
  const paragraphs = (t('about.paragraphs', FALLBACK_PARAGRAPHS) as string[]) ?? FALLBACK_PARAGRAPHS;
  const imageCaptions = (t('about.imageCaptions', FALLBACK_IMAGES) as Record<string, AboutImageCopy>) ?? FALLBACK_IMAGES;

  const images: Array<[string, AboutImageCopy]> = [
    ['soldier', imageCaptions.soldier ?? FALLBACK_IMAGES.soldier],
    ['tunnel', imageCaptions.tunnel ?? FALLBACK_IMAGES.tunnel],
  ];

  return (
    <section className="relative isolate overflow-hidden px-6 py-16">
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <img
          src={berlinBg}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(9,12,15,0.94)_0%,rgba(9,12,15,0.9)_35%,rgba(9,12,15,0.85)_100%)]" aria-hidden="true" />
      </div>
      <div className="mx-auto flex max-w-5xl flex-col gap-12">
        <header className="space-y-8 text-center">
          <div className="relative overflow-hidden rounded-[36px] shadow-[0_42px_120px_-60px_rgba(0,0,0,0.85)]">
            <img
              src={aboutHeroBg}
              alt="Historic checkpoint in Berlin"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(9,12,15,0.82)_0%,rgba(9,12,15,0.35)_100%)]" aria-hidden="true" />
            <div className="relative px-6 py-16 sm:px-12 sm:py-20">
              <h1 className="text-3xl font-heading tracking-wide text-white drop-shadow-[0_8px_18px_rgba(0,0,0,0.55)] sm:text-4xl">
                {heading}
              </h1>
            </div>
          </div>
          <p className="mx-auto max-w-3xl text-base text-[var(--color-text-primary)]/80 sm:text-lg">{intro}</p>
        </header>

        <div className="mx-auto max-w-3xl space-y-5 text-base leading-relaxed text-[var(--color-text-primary)]/80">
          {paragraphs.map((paragraph, index) => (
            <p key={`${paragraph.slice(0, 20)}-${index}`} className="whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </div>

        <section className="grid gap-8 lg:grid-cols-2">
          {images.map(([key, copy]) => {
            const hasImage = Boolean(copy?.src);
            const altText = copy?.alt ?? copy?.heading;
            const placeholderText = copy?.placeholder ?? 'Image coming soon';

            return (
              <figure
                key={key}
                className="glass-surface glass-panel flex flex-col overflow-hidden rounded-[32px]"
              >
                <div className="relative h-80 overflow-hidden sm:h-96">
                  {hasImage ? (
                    <img
                      src={copy?.src}
                      alt={altText}
                      loading="lazy"
                      className="h-full w-full object-cover object-center"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-[rgba(255,255,255,0.06)] text-center text-sm uppercase tracking-[0.24em] text-white/40">
                      {placeholderText}
                    </div>
                  )}
                </div>
                <figcaption className="space-y-2 px-6 py-5">
                  <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{copy?.heading}</h2>
                  <p className="text-sm text-[var(--color-text-primary)]/70">{copy?.body}</p>
                </figcaption>
              </figure>
            );
          })}
        </section>
      </div>
    </section>
  );
}
